<template>
    <v-container fluid>
        <v-row class="align-center pl-3">
            <!-- Breadcrumbs -->
            <v-breadcrumbs
                :items="[
                    { text: 'Home', disable: false, href: '/#/' },
                    {
                        text: 'Cost Rates',
                        disable: false,
                        href: '/#/costrates'
                    },
                    { text: 'Product Cost Templates', disable: true }
                ]"
                divider="/"
            ></v-breadcrumbs>
        </v-row>

        <v-row class="ma-0">
            <v-col cols="12" class="pt-0">
                <v-hover>
                    <template v-slot="{ hover }">
                        <v-card :elevation="hover ? 6 : 1">
                            <v-card-title class="pb-0">
                                <v-icon color="primary" class="mx-2"
                                    >euro_symbol</v-icon
                                >Product Cost Templates
                                <v-spacer></v-spacer>
                                <v-col cols="12" sm="4">
                                    <v-row justify="end" align="end">
                                        <v-text-field
                                            dense
                                            v-model="search"
                                            label="Search"
                                            clearable
                                            hide-details
                                            class="mx-2"
                                        ></v-text-field>
                                    </v-row>
                                </v-col>
                                <v-spacer></v-spacer>
                                <v-btn
                                    v-if="hasPermission('add_product')"
                                    color="primary"
                                    dark
                                    outlined
                                    rounded
                                    small
                                    class="mx-2"
                                    @click="openNewTemplateDialog()"
                                    >New Template</v-btn
                                >
                            </v-card-title>

                            <v-container fluid>
                                <v-data-table
                                    :headers="headers"
                                    :items="
                                        tenantProductFixedCostTemplates
                                            ? tenantProductFixedCostTemplates
                                            : []
                                    "
                                    :search="search"
                                    :loading="
                                        $apollo.queries
                                            .tenantProductFixedCostTemplates
                                            .loading
                                    "
                                    disable-pagination
                                    hide-default-footer
                                >
                                    <template
                                        v-slot:item.node.totalCost="{
                                            item
                                        }"
                                    >
                                        €{{
                                            item.node.totalCost.toLocaleString()
                                        }}
                                    </template>
                                    <template v-slot:item.action="{ item }">
                                        <v-row class="justify-center">
                                            <v-btn
                                                v-if="
                                                    hasPermission(
                                                        'change_product'
                                                    )
                                                "
                                                color="grey darken-2"
                                                icon
                                                outlined
                                                small
                                                class="ml-1"
                                                @click="
                                                    copyProductFixedCostTemplate(
                                                        item
                                                    )
                                                "
                                            >
                                                <v-icon small>file_copy</v-icon>
                                            </v-btn>
                                            <v-btn
                                                icon
                                                outlined
                                                small
                                                class="ml-1"
                                                color="primary"
                                                @click="
                                                    openEditTemplateDialog(
                                                        item.node
                                                    )
                                                "
                                            >
                                                <v-icon small>edit</v-icon>
                                            </v-btn>
                                            <v-btn
                                                icon
                                                outlined
                                                small
                                                color="error"
                                                class="ml-1"
                                                @click="
                                                    onDeletingTemplate(
                                                        item.node
                                                    )
                                                "
                                            >
                                                <v-icon small>delete</v-icon>
                                            </v-btn>
                                        </v-row>
                                    </template>
                                </v-data-table>
                            </v-container>
                        </v-card>
                    </template>
                </v-hover>
            </v-col>
        </v-row>

        <!-- Delete Dialog -->
        <DeleteDialog
            :dialog.sync="deleteProductFixedCostTemplateDialog"
            :item="selectedProductFixedCostTemplate"
            v-on:confirmed="deleteTemplateConfirmed"
        >
            <template v-slot:title>Delete Selected Templates</template>
            <template v-slot:content
                >Are you sure you want to delete selected templates?
                <br />
            </template>
        </DeleteDialog>

        <!-- Add Dialog -->
        <ProductFixedCostTemplateEditDialog
            :dialog.sync="newTemplateDialog"
            :object.sync="selectedTemplate"
            :isCopy.sync="isCopyProductFixedCostTemplate"
            v-on:item-created="onTemplateCreated"
        ></ProductFixedCostTemplateEditDialog>
    </v-container>
</template>

<script>
import gql from "graphql-tag";
import helper from "@/utils/helper.js";
import DeleteDialog from "@/components/base/DeleteDialog.vue";
import ProductFixedCostTemplateEditDialog from "@/components/cost/ProductFixedCostTemplateEditDialog.vue";

export default {
    name: "product-fixed-cost-templates-page",
    components: { DeleteDialog, ProductFixedCostTemplateEditDialog },

    apollo: {
        tenantProductFixedCostTemplates: {
            query: gql`
                query tenantProductFixedCostTemplates {
                    tenantProductFixedCostTemplates {
                        edges {
                            node {
                                id
                                templateName
                                totalCost
                                productFixedCostTemplateItemSet(
                                    orderBy: "itemName"
                                ) {
                                    totalCount
                                    edges {
                                        node {
                                            id
                                            itemName
                                            costValue
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables() {
                return {};
            },
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => {
                return data.tenantProductFixedCostTemplates.edges.sort(
                    (a, b) => {
                        return a.node.templateName.toLowerCase() ==
                            b.node.templateName.toLowerCase()
                            ? 0
                            : a.node.templateName.toLowerCase() >
                              b.node.templateName.toLowerCase()
                            ? 1
                            : -1;
                    }
                );
            },
            skip: false
            // pollInterval: 60000 // ms
        }
    },

    data() {
        return {
            newTemplateDialog: false,
            isCopyProductFixedCostTemplate: false,
            deleteProductFixedCostTemplateDialog: false,
            isDeletingProductFixedCostTemplate: false,
            selectedProductFixedCostTemplate: null,
            selectedTemplate: null,
            search: null,
            headers: [
                {
                    text: "Template Name",
                    align: "start",
                    sortable: true,
                    value: "node.templateName"
                },
                {
                    text: "Cost (Per Product)",
                    value: "node.totalCost",
                    align: "end"
                },
                {
                    text: "#Items",
                    value: "node.productFixedCostTemplateItemSet.totalCount",
                    align: "end"
                },
                {
                    text: "Actions",
                    value: "action",
                    width: "120px",
                    align: "center",
                    sortable: false
                }
            ],
            rules: {
                required: v => !helper.isEmpty(v) || "This field is required",
                listRequired: v =>
                    (v || "").length >= 1 || `This field is required`,
                minLength: len => v =>
                    (v || "").length >= len || `Require at least ${len}`,
                maxLength: len => v => (v || "").length <= len || "Too long"
            }
        };
    },
    computed: {},
    watch: {},
    created() {
        this.hasPermission = helper.hasPermission;
    },
    methods: {
        refreshProductFixedCostTemplates() {
            this.$apollo.queries.tenantProductFixedCostTemplates.refresh();
        },

        copyProductFixedCostTemplate(item) {
            this.isCopyProductFixedCostTemplate = true;
            this.selectedTemplate = item.node;
            this.newTemplateDialog = true;
        },

        openNewTemplateDialog() {
            this.isCopyProductFixedCostTemplate = false;
            this.newTemplateDialog = true;
        },

        onTemplateCreated() {
            this.refreshProductFixedCostTemplates();
        },

        openEditTemplateDialog(template) {
            this.selectedTemplate = template;
            this.isCopyProductFixedCostTemplate = false;
            this.newTemplateDialog = true;
        },

        onDeletingTemplate(template) {
            this.selectedProductFixedCostTemplate = template;
            this.deleteProductFixedCostTemplateDialog = true;
        },

        deleteTemplateConfirmed() {
            this.isDeletingProductFixedCostTemplate = true;

            // console.log(_operatingCostList);

            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation deleteProductFixedCostTemplate(
                            $input: DeleteProductFixedCostTemplateInput!
                        ) {
                            deleteProductFixedCostTemplate(input: $input) {
                                productFixedCostTemplate {
                                    id
                                    templateName
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: {
                            id: this.selectedProductFixedCostTemplate.id
                        }
                    }
                })
                .then(() => {
                    this.selectedProductFixedCostTemplate = null;
                    this.refreshProductFixedCostTemplates();

                    // show snackbar
                    const payload = {
                        color: "success",
                        message: `Deleted!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .catch(error => {
                    // this.isLoadingPlannedTaskPhases = false;

                    // Error
                    console.error(error);

                    // show snackbar
                    const payload = {
                        color: "error",
                        message: `Failed!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .finally(() => {
                    this.isDeletingProductFixedCostTemplate = false;
                    this.deleteProductFixedCostTemplateDialog = false;
                });
        }
    }
};
</script>